import React from 'react';

import { formatCurrency } from '~libs/dataUtils';
import { splitOverLimit, withinLimit } from '~support-at-home/lib/overLimits';

import DonutFiguresCard from '~support-at-home/components/common/DonutFiguresCard';
import SectionInfo from '~support-at-home/components/common/SectionInfo';

import { TrackerCaseManagement } from '~support-at-home/models';

import { TrackerLegendItem } from './TrackerLegend';
import { TRACKER_FIGURE_COLOURS } from './trackerPalette';
import { TRACKER_TEXT } from './trackerText';
import { describeCareManagement, OverLap, toCareManagementWithinSegments, toOverLapSegments } from './transforms';

interface ComponentProps {
  title?: string;
  description?: string;
  data?: TrackerCaseManagement;
}

const CaseManagementOverview: React.FC<ComponentProps> = ({
  title = TRACKER_TEXT.careManagement.title,
  description = TRACKER_TEXT.careManagement.subtitle,
  data,
}) => {
  const copy = TRACKER_TEXT.careManagement;
  const allowance = data?.allowance ?? 0;
  const singles = data?.counted.singles ?? 0;
  const repeats = data?.counted.repeat_occurrences ?? 0;
  const within = withinLimit(allowance, { booked: data?.booked ?? 0, claimed: data?.claimed ?? 0 });
  const over = splitOverLimit(allowance, { booked: data?.booked ?? 0, claimed: data?.claimed ?? 0 });
  const lap: OverLap = {
    key: 'over-allowance',
    label: copy.legend.over,
    color: TRACKER_FIGURE_COLOURS.careManagementOver,
  };

  // Used is the amount within the allowance, with the part beyond it in its own row, so it never reads as a larger
  // total; the ring stops at the allowance and the part beyond it is the second lap
  const figures: TrackerLegendItem[] = [
    {
      key: 'used',
      label: copy.legend.used,
      value: within.booked + within.claimed,
      color: TRACKER_FIGURE_COLOURS.careManagementUsed,
      caption: { id: 'cm-used', content: copy.used(singles, repeats) },
    },
    {
      key: 'unused',
      label: copy.legend.unused,
      value: within.unused,
      color: TRACKER_FIGURE_COLOURS.careManagementRemaining,
      caption: { id: 'cm-unused', content: copy.unused },
    },
    ...(over.total > 0 ? [{ ...lap, value: over.total, caption: { id: 'cm-over', content: copy.over } }] : []),
  ];

  return (
    <DonutFiguresCard
      title={title}
      description={description}
      titleAddon={<SectionInfo id="cm-section" label={title} content={copy.section} />}
      over={over.total > 0}
      dataCy="tracker-cm-card"
      // No care management on this budget: an empty ring with a dash, and N/A where the figures would be
      donut={
        data
          ? {
              segments: toCareManagementWithinSegments(within),
              overSegments: toOverLapSegments(over.total, lap),
              centreValue: formatCurrency(allowance, 0),
              centreLabel: copy.centreLabel,
              percentBase: allowance,
              ariaLabel: describeCareManagement(data),
              dataCy: 'tracker-cm-donut',
            }
          : undefined
      }
      legend={figures}
      emptyLabel={copy.noCareManagementLabel}
      emptyText={copy.noCareManagement}
      emptyDataCy="tracker-cm-not-applicable"
    />
  );
};

export default CaseManagementOverview;
